import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { notifyXp } from '@/lib/xpToast';
import { Wind, ArrowLeft, Play, Square, Volume2, VolumeX } from 'lucide-react';

// 4-7-8 breathing: 4s inhale, 7s hold, 8s exhale = 19s cycle
const PHASES = [
  { name: 'Inhale', secs: 4, scale: 1.6, color: 'rgba(212,164,55,0.85)' },
  { name: 'Hold',   secs: 7, scale: 1.6, color: 'rgba(255,255,255,0.85)' },
  { name: 'Exhale', secs: 8, scale: 1.0, color: 'rgba(58,111,176,0.85)' },
];

export default function BreathRhythm() {
  const { user } = useAuth();
  const [duration, setDuration] = useState(3);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [phaseElapsed, setPhaseElapsed] = useState(0);
  const [sound, setSound] = useState(true);
  const audioRef = useRef(null);
  const totalSecs = duration * 60;

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setElapsed((e) => {
        const next = e + 0.1;
        if (next >= totalSecs) { finish(); return totalSecs; }
        return next;
      });
      setPhaseElapsed((pe) => {
        const phase = PHASES[phaseIdx];
        if (pe + 0.1 >= phase.secs) {
          setPhaseIdx((i) => (i + 1) % PHASES.length);
          return 0;
        }
        return pe + 0.1;
      });
    }, 100);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, phaseIdx]);

  useEffect(() => {
    if (!running || !sound) { stopTone(); return; }
    // Play a tone that matches phase
    const phase = PHASES[phaseIdx];
    playTone(phase.name === 'Inhale' ? 220 : phase.name === 'Hold' ? 0 : 165);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseIdx, running, sound]);

  const start = () => { setElapsed(0); setPhaseIdx(0); setPhaseElapsed(0); setRunning(true); };
  const stop = () => { setRunning(false); stopTone(); };
  const finish = async () => {
    setRunning(false); stopTone();
    if (user) {
      try {
        const r = await api.post('/games/record', { game: 'breath_rhythm', minutes: duration });
        notifyXp(r.data.xp_result, `Breath \u00B7 ${duration} min`);
        window.dispatchEvent(new Event('kc:xp-updated'));
      } catch {}
    }
  };

  const phase = PHASES[phaseIdx];
  const pct = totalSecs > 0 ? (elapsed / totalSecs) * 100 : 0;

  return (
    <div className="bg-ink text-white min-h-screen">
      <div className="kc-container py-6">
        <Link to="/games" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-white/60 hover:text-[color:var(--kc-gold)]" data-testid="back-games"><ArrowLeft size={12}/> Games</Link>
        <div className="mt-4 text-center">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold"><Wind size={12}/> 4-7-8 Breath</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Breath Rhythm</h1>
          <p className="mt-2 text-white/65 max-w-md mx-auto">Inhale 4 · Hold 7 · Exhale 8. Calms the nervous system. Just follow the orb.</p>
        </div>

        <div className="mt-8 flex items-center justify-center">
          <div className="relative w-[280px] h-[280px] md:w-[360px] md:h-[360px] flex items-center justify-center" data-testid="br-arena">
            <div className="absolute inset-0 rounded-full opacity-20 blur-3xl transition-all duration-[4000ms]" style={{ background: phase.color, transform: `scale(${running ? phase.scale : 1})` }}/>
            <div className="relative rounded-full w-40 h-40 md:w-56 md:h-56 transition-all duration-[4000ms] ease-in-out" style={{ background: phase.color, transform: `scale(${running ? phase.scale * 0.6 : 1})`, boxShadow: '0 0 80px rgba(212,164,55,0.4)' }}/>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="font-display uppercase text-3xl md:text-4xl tracking-wide" data-testid="br-phase">{running ? phase.name : 'Ready'}</div>
              {running && <div className="mt-1 text-sm text-white/70 tabular-nums">{Math.max(0, Math.ceil(phase.secs - phaseElapsed))}s</div>}
            </div>
          </div>
        </div>

        <div className="mt-8 max-w-md mx-auto">
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-[color:var(--kc-gold)] transition-all" style={{ width: `${pct}%` }} data-testid="br-progress"/>
          </div>
          <div className="mt-1 text-center text-xs text-white/60 tabular-nums">{Math.floor(elapsed/60)}:{String(Math.floor(elapsed%60)).padStart(2,'0')} / {duration}:00</div>
        </div>

        {!running ? (
          <div className="mt-6 flex flex-col items-center gap-3">
            <div className="flex items-center gap-2" data-testid="br-duration">
              {[1, 3, 5].map(d => (
                <button key={d} onClick={() => setDuration(d)} className={`px-4 h-10 rounded-full text-sm font-semibold ${duration === d ? 'bg-[color:var(--kc-gold)] text-black' : 'bg-white/10 hover:bg-white/20'}`} data-testid={`br-dur-${d}`}>{d} min</button>
              ))}
            </div>
            <button onClick={start} className="h-13 px-8 inline-flex items-center gap-2 rounded-full bg-[color:var(--kc-gold)] text-black font-semibold text-lg hover:bg-[color:var(--kc-gold-2)]" data-testid="br-start"><Play size={18} fill="currentColor"/> Begin</button>
            <button onClick={() => setSound(!sound)} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-white/60 hover:text-white" data-testid="br-sound">
              {sound ? <Volume2 size={12}/> : <VolumeX size={12}/>} Sound {sound ? 'on' : 'off'}
            </button>
          </div>
        ) : (
          <div className="mt-6 flex justify-center">
            <button onClick={stop} className="h-12 px-6 inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 font-semibold" data-testid="br-stop"><Square size={14} fill="currentColor"/> Stop</button>
          </div>
        )}
      </div>
    </div>
  );
}

let ctx = null, osc = null, gainNode = null;
function playTone(freq) {
  if (freq === 0) { stopTone(); return; }
  if (!ctx) { try { ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch { return; } }
  if (!osc) {
    osc = ctx.createOscillator(); gainNode = ctx.createGain();
    osc.type = 'sine'; gainNode.gain.value = 0.02;
    osc.connect(gainNode); gainNode.connect(ctx.destination); osc.start();
  }
  osc.frequency.setTargetAtTime(freq, ctx.currentTime, 0.5);
}
function stopTone() {
  if (osc) { try { osc.stop(); } catch {}; osc.disconnect(); gainNode.disconnect(); osc = null; gainNode = null; }
}
